// src/pages/CartPage.tsx - VERSION CORRIGÉE avec frais de livraison
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import cartService, { CartItem } from '../services/cartService';
import productService from '../services/productService';
import { 
  ArrowLeft, 
  ShoppingCart, 
  Plus, 
  Minus,
  Trash2,
  CreditCard,
  Package,
  AlertCircle,
  CheckCircle,
  Eye,
  RefreshCw,
  Truck
} from 'lucide-react';

const CartPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removingItem, setRemovingItem] = useState<number | null>(null);
  const [updatingQuantity, setUpdatingQuantity] = useState<number | null>(null);

  useEffect(() => {
    // ✅ VÉRIFICATION: Seuls les customers peuvent accéder au panier
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!user.is_customer) {
      navigate('/');
      return;
    }
    
    loadCart();
  }, [user, navigate]);

  // Recharger automatiquement quand l'utilisateur revient sur la page
  useEffect(() => {
    const handleFocus = () => loadCart();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const loadCart = async () => {
    try {
      setLoading(true);
      setError('');
      const cartData = await cartService.getCart();
      setCartItems(cartData);
    } catch (error) {
      console.error('Erreur lors du chargement du panier:', error);
      setError('Erreur lors du chargement du panier.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (itemId: number) => {
    const item = cartItems.find(item => item.id === itemId);
    if (!confirm(`Êtes-vous sûr de vouloir supprimer "${item?.product.name}" du panier ?`)) {
      return;
    }

    try {
      setRemovingItem(itemId);
      await cartService.removeFromCart(itemId);
      await loadCart(); // Recharger le panier
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setError('Erreur lors de la suppression de l\'article.');
    } finally {
      setRemovingItem(null);
    }
  };

  const handleQuantityChange = async (itemId: number, newQuantity: number, maxStock: number) => {
    if (newQuantity <= 0) {
      handleRemoveItem(itemId);
      return;
    }

    if (newQuantity > maxStock) {
      alert(`Stock maximum disponible: ${maxStock}`);
      return;
    }

    try {
      setUpdatingQuantity(itemId);
      
      // ✅ MÉTHODE ALTERNATIVE: Supprimer et re-ajouter au lieu de mettre à jour
      const item = cartItems.find(item => item.id === itemId);
      if (!item) {
        throw new Error('Article non trouvé');
      }
      
      // Supprimer l'ancien item
      await cartService.removeFromCart(itemId);
      
      // Ajouter le nouvel item avec la bonne quantité
      await cartService.addToCart(item.product_id, newQuantity);
      
      await loadCart(); // Recharger le panier
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      setError('Erreur lors de la mise à jour de la quantité.');
      await loadCart(); // Recharger en cas d'erreur
    } finally {
      setUpdatingQuantity(null);
    }
  };

  // ✅ NOUVELLE MÉTHODE: Redirige vers CheckoutPage au lieu d'appeler checkout()
  const handleProceedToCheckout = () => {
    const { canCheckout, errors } = cartService.canCheckout(cartItems);
    
    if (!canCheckout) {
      setError(errors.join(', '));
      return;
    }

    // Vérifier que tous les produits viennent du même farmer
    const farmerIds = new Set(cartItems.map(item => item.product.owner_id).filter(Boolean));
    if (farmerIds.size > 1) {
      setError(`Votre panier contient des produits de ${farmerIds.size} producteurs différents. Veuillez effectuer une commande par producteur.`);
      return;
    }

    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    navigate('/products');
  };

  // ✅ NOUVELLE FONCTIONNALITÉ: Vider tout le panier
  const handleClearCart = async () => {
    if (!confirm('Êtes-vous sûr de vouloir vider complètement votre panier ?')) {
      return;
    }

    try {
      setLoading(true);
      // Supprimer tous les articles un par un
      const promises = cartItems.map(item => cartService.removeFromCart(item.id));
      await Promise.all(promises);
      await loadCart();
    } catch (error) {
      console.error('Erreur lors du vidage du panier:', error);
      setError('Erreur lors du vidage du panier.');
    }
  };

  // ✅ CORRECTION PRINCIPALE: Utiliser les vraies méthodes de calcul
  const cartTotal = cartService.calculateCartTotal(cartItems);
  const itemsCount = cartService.calculateCartItemsCount(cartItems);
  const deliveryFee = cartService.calculateDeliveryFee(cartItems); // ✅ CORRECTION ICI
  const finalTotal = cartService.calculateFinalTotal(cartItems, deliveryFee);
  
  // ✅ NOUVEAU: Obtenir les détails des frais de livraison
  const deliveryBreakdown = cartService.getDeliveryFeeBreakdown(cartItems);
  const hasDeliveryFees = cartService.hasDeliveryFees(cartItems);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-olive border-r-transparent"></div>
          <p className="mt-2">Chargement du panier...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-beige min-h-screen">
      {/* ✅ HEADER avec navigation améliorée */}
      <div className="flex items-center justify-between mb-6">
        <Link 
          to="/products"
          className="flex items-center gap-2 text-slate hover:text-terracotta transition duration-300"
        >
          <ArrowLeft className="h-5 w-5" />
          Continuer mes achats
        </Link>
      </div>

      <div className="flex items-center gap-3 mb-8">
        <ShoppingCart className="h-8 w-8 text-olive" />
        <h1 className="text-3xl font-bold text-slate">Mon panier</h1>
        {itemsCount > 0 && (
          <span className="bg-olive text-white px-3 py-1 rounded-full text-sm">
            {itemsCount} article{itemsCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* ✅ MESSAGE D'ERREUR */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
          <button 
            onClick={() => setError('')}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {cartItems.length === 0 ? (
        /* ✅ PANIER VIDE - Interface améliorée */
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <div className="max-w-md mx-auto">
            <div className="h-24 w-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <ShoppingCart className="h-12 w-12 text-gray-400" />
            </div>
            <h2 className="text-2xl font-semibold text-slate mb-2">Votre panier est vide</h2>
            <p className="text-gray-600 mb-6">
              Découvrez nos produits frais et locaux pour commencer vos achats.
            </p>
            <button
              onClick={handleContinueShopping}
              className="bg-olive text-white px-6 py-3 rounded-md hover:bg-terracotta transition duration-300"
            >
              Découvrir nos produits
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* ✅ ARTICLES DU PANIER - Interface améliorée */}
          <div className="lg:col-span-2 space-y-4">
            {/* Header avec bouton vider panier */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-slate">Vos articles</h2>
              {cartItems.length > 1 && (
                <button
                  onClick={handleClearCart}
                  className="text-red-600 hover:text-red-700 text-sm transition duration-300"
                >
                  Vider le panier
                </button>
              )}
            </div>
            
            {cartItems.map((item) => {
              // ✅ NOUVEAU: Calculer le coût total de l'item (prix + livraison)
              const itemCost = cartService.getItemTotalCost(item);
              
              return (
                <div key={item.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
                  <div className="flex gap-4">
                    {/* ✅ IMAGE PRODUIT */}
                    <div className="h-24 w-24 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                      {item.product.image_url ? (
                        <img 
                          src={productService.getImageUrl(item.product.image_url)} 
                          alt={item.product.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center">
                          <Package className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* ✅ INFORMATIONS PRODUIT */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-semibold text-slate">
                          {item.product.name}
                        </h3>
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          disabled={removingItem === item.id}
                          className="flex items-center gap-1 text-red-600 hover:text-red-700 transition duration-300 disabled:opacity-50"
                          title="Supprimer cet article"
                        >
                          {removingItem === item.id ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent"></div>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      
                      {/* ✅ NOUVEAU: Affichage des prix avec détails de livraison */}
                      <div className="space-y-1 mb-3">
                        <p className="text-gray-600 text-sm">
                          Prix unitaire: {cartService.formatPrice(itemCost.unitPrice)}
                        </p>
                        {itemCost.deliveryFee > 0 && (
                          <p className="text-gray-500 text-xs flex items-center gap-1">
                            <Truck className="h-3 w-3" />
                            Livraison: {cartService.formatPrice(itemCost.deliveryFee)}
                          </p>
                        )}
                        {itemCost.deliveryFee === 0 && (
                          <p className="text-green-600 text-xs flex items-center gap-1">
                            <Truck className="h-3 w-3" />
                            Livraison gratuite
                          </p>
                        )}
                      </div>
                      
                      {/* ✅ CONTRÔLES DE QUANTITÉ améliorés */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-slate">Quantité:</span>
                          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1">
                            <button
                              onClick={() => handleQuantityChange(item.id, item.quantity - 1, 999)}
                              disabled={updatingQuantity === item.id || item.quantity <= 1}
                              className="p-1 rounded hover:bg-white disabled:opacity-50 transition duration-300"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            
                            <span className="w-12 text-center font-medium">
                              {updatingQuantity === item.id ? (
                                <div className="h-4 w-4 mx-auto animate-spin rounded-full border-2 border-olive border-t-transparent"></div>
                              ) : (
                                item.quantity
                              )}
                            </span>
                            
                            <button
                              onClick={() => handleQuantityChange(item.id, item.quantity + 1, 999)}
                              disabled={updatingQuantity === item.id}
                              className="p-1 rounded hover:bg-white disabled:opacity-50 transition duration-300"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* ✅ TOTAL ARTICLE AVEC LIVRAISON */}
                        <div className="text-right">
                          <div className="text-lg font-semibold text-terracotta">
                            {cartService.formatPrice(itemCost.totalForQuantity)}
                          </div>
                          {item.quantity > 1 && (
                            <div className="text-xs text-gray-500">
                              {item.quantity} × {cartService.formatPrice(itemCost.unitPrice)}
                              {itemCost.deliveryFee > 0 && ' + livraison'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ✅ RÉSUMÉ DU PANIER - Interface corrigée avec vrais frais de livraison */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-slate mb-4">Résumé du panier</h2>
              
              {/* ✅ DÉTAILS DES CALCULS CORRIGÉS */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    Articles ({itemsCount} article{itemsCount !== 1 ? 's' : ''})
                  </span>
                  <span className="font-medium">{cartService.formatPrice(cartTotal)}</span>
                </div>
                
                {/* ✅ CORRECTION: Affichage correct des frais de livraison */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 flex items-center gap-1">
                    <Truck className="h-3 w-3" />
                    Livraison
                  </span>
                  <span className={`font-medium ${deliveryFee > 0 ? 'text-gray-700' : 'text-green-600'}`}>
                    {deliveryFee > 0 ? cartService.formatPrice(deliveryFee) : 'Gratuite'}
                  </span>
                </div>
                
                {/* ✅ NOUVEAU: Détail des frais de livraison si applicable */}
                {hasDeliveryFees && deliveryBreakdown.breakdown.length > 0 && (
                  <div className="text-xs text-gray-500 pl-4 space-y-1">
                    {deliveryBreakdown.breakdown
                      .filter(item => !item.isFree)
                      .map((item, index) => (
                        <div key={index} className="flex justify-between">
                          <span>{item.productName.slice(0, 20)}...</span>
                          <span>{cartService.formatPrice(item.fee)}</span>
                        </div>
                      ))
                    }
                    {deliveryBreakdown.freeDeliveryItems > 0 && (
                      <div className="text-green-600">
                        {deliveryBreakdown.freeDeliveryItems} produit{deliveryBreakdown.freeDeliveryItems > 1 ? 's' : ''} avec livraison gratuite
                      </div>
                    )}
                  </div>
                )}
                
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-lg font-semibold">
                    <span className="text-slate">Total</span>
                    <span className="text-terracotta">{cartService.formatPrice(finalTotal)}</span>
                  </div>
                </div>
              </div>

              {/* ✅ BOUTONS D'ACTION */}

              {/* Bloc OYE-Consulting */}
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="text-xl">🤝</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900 text-sm mb-1">Besoin d'aide ? BeninAgri</h3>
                    <p className="text-xs text-amber-800 mb-2">
                      Livraison, sourcing, représentation auprès du vendeur.
                    </p>
                    <div className="flex gap-2">
                      <a href="https://wa.me/22901095040802" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 transition duration-300">
                        WhatsApp
                      </a>
                      <a href="mailto:contact@beninagri.com" className="flex items-center gap-1 bg-amber-600 text-white px-3 py-1 rounded text-xs hover:bg-amber-700 transition duration-300">
                        Email
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {/* ✅ BOUTON PRINCIPAL: Redirige vers CheckoutPage */}
                <button
                  onClick={handleProceedToCheckout}
                  className="w-full bg-olive text-white py-3 px-4 rounded-md hover:bg-terracotta transition duration-300 flex items-center justify-center gap-2 font-medium"
                >
                  <CreditCard className="h-5 w-5" />
                  Procéder au paiement
                </button>
                
                <button
                  onClick={handleContinueShopping}
                  className="w-full bg-gray-200 text-slate py-3 px-4 rounded-md hover:bg-gray-300 transition duration-300"
                >
                  Continuer mes achats
                </button>
              </div>

              {/* ✅ INFORMATIONS DE SÉCURITÉ MISES À JOUR */}
              <div className="mt-6 space-y-3">
                <div className="p-3 bg-green-50 rounded-md">
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <CheckCircle className="h-4 w-4" />
                    <span>Paiement sécurisé</span>
                  </div>
                </div>

                <div className="p-3 bg-blue-50 rounded-md">
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <Truck className="h-4 w-4" />
                    <span>
                      {deliveryFee > 0 
                        ? `Frais de livraison: ${cartService.formatPrice(deliveryFee)}`
                        : 'Livraison gratuite'
                      }
                    </span>
                  </div>
                </div>

                {/* ✅ NOUVEAU: Statistiques de livraison */}
                {hasDeliveryFees && (
                  <div className="p-3 bg-yellow-50 rounded-md">
                    <div className="text-xs text-yellow-700">
                      <div>{deliveryBreakdown.paidDeliveryItems} produit{deliveryBreakdown.paidDeliveryItems > 1 ? 's' : ''} avec frais de livraison</div>
                      {deliveryBreakdown.freeDeliveryItems > 0 && (
                        <div>{deliveryBreakdown.freeDeliveryItems} produit{deliveryBreakdown.freeDeliveryItems > 1 ? 's' : ''} avec livraison gratuite</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ✅ AIDE */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">
                  Besoin d'aide ? <Link to="/contact" className="text-olive hover:underline">Contactez-nous</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;